import './style/Payouts.css';
import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { openTrayTypeWithData } from '../../redux/keystoneSlice';
import Placeholder from './Placeholder';
import { useEvent as useEffectEvent } from '@reactuses/core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchPayouts, normalizeTabName } from '../../redux/keystoneInterfaceSlice';
import { Text } from '@harperai/materialize-design-system';
import { colorFromNumber, formatToCurrency } from '../../util/Calculate';
import PayoutOnhold from '../../media/payout_onhold.png';
import PayoutSent from '../../media/payout_sent.png';

const Payouts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const keystoneInterfacePayoutsTabs = useSelector((state) => state?.keystoneInterface?.payoutsTabs);
    const keystoneInterfaceAccessToken = useSelector((state) => state?.keystoneInterface?.accessToken);
    const keystoneInventoryPayouts = useSelector((state) => state?.keystoneInventory?.payouts);
    const keystoneInterfaceFetchedPayouts = useSelector((state) => state?.keystoneInterface?.fetchedPayouts);
    const { page, tabName } = useParams();

    const keystoneInterfacePayoutsTab = keystoneInterfacePayoutsTabs.map(normalizeTabName).indexOf(normalizeTabName(tabName));

    const { search } = useLocation();
    const selectedObjectId = new URLSearchParams(search).get('id') || null;
    const selectedObject = useMemo(() => keystoneInventoryPayouts?.find(payout => payout.id === selectedObjectId), [keystoneInventoryPayouts, selectedObjectId]);

    useEffect(() => {
        fetchPayoutData();
    }, []);

    useEffect(() => {
        if (keystoneInterfacePayoutsTab < 0 && !selectedObjectId) {
            navigate(`/${page}/${normalizeTabName(keystoneInterfacePayoutsTabs?.[0])}`);
        }
    }, [keystoneInterfacePayoutsTab, keystoneInterfacePayoutsTabs, page, navigate, selectedObjectId]);

    useEffect(() => {
        const matchingPayout = keystoneInventoryPayouts?.find(offer => offer.id === selectedObjectId);
        if (!matchingPayout && selectedObjectId && keystoneInterfaceFetchedPayouts) {
            navigate(`/${page}/${tabName}`);
        } else if (matchingPayout?.status === "ON_HOLD" && tabName !== "on-hold") {
            navigate(`/${page}/on-hold?id=${matchingPayout.id}`);
        } else if (matchingPayout?.status === "SENT" && tabName !== "sent") {
            navigate(`/${page}/sent?id=${matchingPayout.id}`);
        }
    }, [keystoneInterfaceFetchedPayouts, keystoneInventoryPayouts, navigate, page, selectedObjectId, tabName]);

    const fetchPayoutData = useEffectEvent(() => {
        dispatch(fetchPayouts({ accessToken: keystoneInterfaceAccessToken }));
    });

    useEffect(() => {
        if (selectedObject) {
            dispatch(openTrayTypeWithData({
                trayOpen: true,
                trayData: selectedObject,
                trayType: "payout"
            }));
        }
    }, [dispatch, selectedObject]);

    const handleOnClick = useCallback((shipment) => {
        navigate(`/${page}/${tabName}?id=${shipment.id}`);
    }, [navigate, page, tabName]);

    const PayoutIcon = ({ payout }) => {
        const { hue, sat, light } = colorFromNumber(payout.offer?.offer_number);
        const tint = `hsl(${hue} ${sat}% ${light}%)`;
        const src =
            tabName === 'on-hold'   ? PayoutOnhold : PayoutSent;

        return (
            <div style={{position:'relative', width:64}}>
                <img
                    src={src}
                    style={{width:'100%', display:'block'}}
                    alt=""
                />

                <div
                    style={{
                        position:'absolute',
                        inset:0,
                        backgroundColor:tint,
                        mixBlendMode:'color',
                        borderRadius:6,
                        pointerEvents:'none',
                        opacity: 0.8
                    }}
                />
            </div>
        );
    };

    return (
        <div className="keystone-shipments list-container product-vertical-container gap-l flex-1 overflow-auto">
            <div className="pad-l no-pad-bot">
                <Text header={2} size="h2" bold={true}>{tabName === "on-hold" ? "On Hold" : tabName === "sent" ? "Sent" : "Pending"}</Text>
            </div>
            <div className="flex vertical pad-m gap-m no-pad-top overflow-auto">
                {keystoneInventoryPayouts?.filter(payout =>
                    (keystoneInterfacePayoutsTab === 0 && payout.status === "ON_HOLD") || 
                    (keystoneInterfacePayoutsTab === 1 && payout.status === "SENT")
                ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).map((payout, index) => (
                    <div className={`flex horizontal keystone-card keystone-row ${selectedObjectId === payout.id ? "keystone-row-active" : ""} space-between pad-m clickable`} key={index} onClick={(e) => handleOnClick(payout)}>
                        <div className="flex horizontal gap-l">
                            <PayoutIcon payout={payout} />
                            <div className="flex vertical">
                                <Text size="paragraph" mono={true} bold={true}>{payout.payout_number.toString().padStart(12,'0').replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')}</Text>
                                <Text size="caption" variant="secondary" bold={true}>Funds Secured: {new Date(new Date(payout.created_at).getTime()).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" })}</Text>
                            </div>
                        </div>
                        <div className="flex vertical align-end">
                            <Text size="paragraph" mono={true} bold={true}>{formatToCurrency(payout.amount)}</Text>
                        </div>
                    </div>
                ))}
            </div>
            {(keystoneInterfacePayoutsTab === 0 &&
            !keystoneInventoryPayouts?.some(payout => (
                payout.status === "ON_HOLD"
            ))) && (
                <Placeholder label="You're all caught up!" />
            )}
            {(keystoneInterfacePayoutsTab === 1 && !keystoneInventoryPayouts?.some(payout => payout.status === "SENT" )) && (
                <Placeholder label="You're all caught up!" />
            )}
        </div>
    );
};

export default React.memo(Payouts);